import { useEffect, useRef, useState } from 'react'
import Spinner from 'react-bootstrap/Spinner'
import { AllTypeaheadOwnAndInjectedProps, ClearButton, Typeahead as BSTypeahead } from 'react-bootstrap-typeahead'

import Icon, { EDHRECIconsType } from '@/components/Icon/Icon'
import typeaheadService, { ScryfallTagType } from '@/services/typeahead.service'
import { SetType } from '@/store/misc/initialState'
import { formatUrl } from '@/utils/helpers'

import styles from './Typeahead.module.scss'

// Typeahead deals in one of the following typescript types, based on the type prop
type typeaheadDataTypes = string | SetType | ScryfallTagType

const filterBy = (option: typeaheadDataTypes, props: AllTypeaheadOwnAndInjectedProps<typeaheadDataTypes>) => {
  const helper = (option: string) => formatUrl(option).includes(formatUrl(props.text))
  return typeof option === 'string' ? helper(option) : Object.values(option).some((value) => helper(value))
}

type Props = {
  className?: string
  clearButton?: boolean
  clearOnChange?: boolean
  icon?: EDHRECIconsType
  onChange: (selected: typeaheadDataTypes[]) => void
  placeholder: string
  selected?: string[]
  showLoading?: boolean
  spinner?: boolean
  type?: string
  value?: string
}

const Typeahead = ({ className, clearButton, clearOnChange, icon, onChange, placeholder, selected, type }: Props) => {
  const ref = useRef<BSTypeahead<typeaheadDataTypes>>(null)

  const [isLoading, setIsLoading] = useState(false)

  const [options, setOptions] = useState<typeaheadDataTypes[]>([])

  const loadTypeahead = async () => {
    setIsLoading(true)

    if (type === 'commanders') {
      setOptions(await (await typeaheadService.getCommanders()).json())
    } else if (type === 'partners') {
      setOptions(await (await typeaheadService.getPartners()).json())
    } else if (type === 'sets') {
      setOptions(await (await typeaheadService.getSets()).json())
    } else if (type === 'tags') {
      setOptions(await typeaheadService.getTags())
    } else {
      setOptions(await (await typeaheadService.getCards()).json())
    }

    setIsLoading(false)
  }

  useEffect(() => {
    loadTypeahead()
  }, [type])

  return (
    <BSTypeahead
      className={className}
      disabled={isLoading}
      filterBy={filterBy}
      id='searchbar'
      inputProps={{
        autoCapitalize: 'off',
        autoComplete: 'off',
        autoCorrect: 'off',
        spellCheck: false,
        type: 'search',
      }}
      labelKey='name'
      minLength={['sets', 'tags'].includes(type as string) ? 0 : 3}
      multiple={['sets', 'tags'].includes(type as string)}
      onChange={(e) => {
        if (e.length > 0 && typeof e[0] === 'string' && e[0].length > 0) {
          ref.current?.blur()
        }
        if (clearOnChange) {
          ref.current?.clear()
        }
        onChange(e)
      }}
      options={options}
      placeholder={placeholder}
      ref={ref}
      selected={selected}
    >
      {({ onClear }) =>
        clearButton ? (
          <div className={styles.iconContainer}>
            <ClearButton className='color-black' onClick={onClear} />
          </div>
        ) : (
          <div className={styles.iconContainer}>
            {isLoading ? <Spinner animation='border' size='sm' /> : icon ? <Icon icon={icon} /> : null}
          </div>
        )
      }
    </BSTypeahead>
  )
}

export default Typeahead
